<template>
  <div
    class="popup-holder popup-inapp-message"
    v-if="inappEnabled && inappModalStore.current"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>{{ inappModalStore.current.title }}</h2>
        <a
          href="#"
          class="popup-close icon-close"
          aria-label="Close"
          @click.prevent="handleDismiss"
        ></a>
      </div>
      <div class="popup-body">
        <video
          v-if="safeVideoUrl"
          :src="safeVideoUrl"
          :poster="safeImageUrl || undefined"
          class="inapp-message-media"
          autoplay
          muted
          loop
          playsinline
          controls
        ></video>
        <img
          v-else-if="safeImageUrl"
          :src="safeImageUrl"
          alt=""
          class="inapp-message-media"
        />
        <p class="inapp-message-body">{{ inappModalStore.current.body }}</p>
        <ul class="btn-list">
          <li>
            <a
              href="#"
              class="btn btn-secondary-outline"
              @click.prevent="handleDismiss"
            >
              Not now
            </a>
          </li>
          <li v-if="inappModalStore.current.ctaLabel">
            <a href="#" class="btn btn-primary" @click.prevent="handleConvert">
              {{ inappModalStore.current.ctaLabel }}
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useInAppModalStore } from "@/stores/inapp-modal-store";
import { isInAppMessagingEnabled } from "@/services/inapp.service";

// server contract: our own hosted assets only — https on an audioscribe.io host (images AND video)
function safeMediaUrl(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const okHost =
      u.hostname === "audioscribe.io" || u.hostname.endsWith(".audioscribe.io");
    return u.protocol === "https:" && okHost ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Generic IN_APP engagement modal — renders whatever the engagement orchestrator returned for
 * the current view (title/body/CTA are server-provided generic copy; nothing is composed here).
 * Mounted in PopupContainer (feature-gated there too); visibility is store-driven.
 *
 * The "shown" report + one-per-session latch happen HERE, when the modal actually reaches the
 * DOM (store.markRendered) — a message the layout never rendered is never counted as seen.
 * The ESC listener exists only while the modal is visible (no idle runtime behavior).
 */
export default {
  name: "InAppMessageModal",
  data() {
    return { inappEnabled: isInAppMessagingEnabled() };
  },
  computed: {
    ...mapStores(useInAppModalStore),
    safeImageUrl() {
      return safeMediaUrl(this.inappModalStore.current?.imageUrl);
    },
    // video wins when both are set; the image then serves as its poster
    safeVideoUrl() {
      return safeMediaUrl(this.inappModalStore.current?.videoUrl);
    },
  },
  watch: {
    "inappModalStore.current": {
      // flush:'post' → the handler runs AFTER the DOM updated for this change, so the render
      // confirmation can verify the modal element is actually connected (never report a shown
      // that no user could have seen).
      flush: "post",
      handler(val) {
        if (!this.inappEnabled) return;
        if (val) {
          this.confirmRenderIfVisible();
          document.addEventListener("keydown", this.handleKeydown);
        } else {
          document.removeEventListener("keydown", this.handleKeydown);
        }
      },
    },
  },
  mounted() {
    // the store may already hold a message when this layout mounts (ask resolved mid-navigation)
    if (this.inappEnabled && this.inappModalStore.current) {
      this.confirmRenderIfVisible();
      document.addEventListener("keydown", this.handleKeydown);
    }
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    /**
     * Latch + report "shown" ONLY once the modal element is truly in the document. Re-checks
     * after nextTick that the same delivery is still current — a dismiss/supersede in between
     * must not be reported as seen.
     */
    confirmRenderIfVisible() {
      const offered = this.inappModalStore.current?.deliveryId;
      if (!offered) return;
      this.$nextTick(() => {
        const el = this.$el;
        const visible = !!el && el.nodeType === 1 && el.isConnected;
        const stillCurrent =
          this.inappModalStore.current?.deliveryId === offered;
        if (visible && stillCurrent) this.inappModalStore.markRendered();
      });
    },
    handleKeydown(e) {
      if (e.key === "Escape" && this.inappModalStore.current)
        this.handleDismiss();
    },
    handleDismiss() {
      this.inappModalStore.dismiss();
    },
    handleConvert() {
      const ctaPath = this.inappModalStore.convert();
      // strict app-relative path: single leading slash, no protocol-relative //host, no backslashes
      const safe =
        typeof ctaPath === "string" &&
        ctaPath.startsWith("/") &&
        !ctaPath.startsWith("//") &&
        !ctaPath.includes("\\");
      if (safe) this.$router.push(ctaPath).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
// House style for prompt-type popups (mirrors NotificationOptInPopup): narrow centered card,
// centered secondary-color copy, flex button row. The global .popup-holder top-anchors on
// desktop, which reads as broken for an engagement message — center this one vertically.
.popup-inapp-message {
  align-items: center;

  .popup-wrap {
    max-width: 420px;
  }

  // keep the title clear of the ✕ on a narrow card (founder pass: they were touching)
  .popup-head {
    padding-left: 44px;
    padding-right: 44px;

    h2 {
      line-height: 1.3;
    }

    .popup-close {
      right: 14px;
    }
  }

  .popup-body {
    padding: 24px;
  }

  .inapp-message-media {
    display: block;
    width: 100%;
    max-width: 100%;
    border-radius: 8px;
    margin: 0 auto 16px;
  }

  .inapp-message-body {
    text-align: center;
    margin-bottom: 24px;
    color: var(--text-secondary, #6b7280);
    line-height: 1.6;
    white-space: pre-line;
  }

  .btn-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 12px;

    li {
      list-style: none;
      margin: 0;
      padding: 0;
    }
  }
}
</style>
